import React from "react";
import { connect } from "react-redux";
import axios from "axios";

import Popup from "devextreme-react/popup";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel } from "devextreme-react/load-panel";
import { alert, confirm } from "devextreme/ui/dialog";

import routes from "../../assets/apiRoutes.json";
import { MESSAGES } from "../../common/Messages";
import { getApiErrorMessage } from "../../common/concurrency";
import CommonReport from "../../reports/components/CommonReport";
import RecalculateKitUsageGridPreview from "./RecalculateKitUsageGridPreview";
import {
    BUTTON,
    PAGE_TITLE,
    PREVIEW_TITLE,
    REPORT_FULL_NAME,
    REPORT_NAME,
    REQUEST_TIMEOUT_MS,
} from "./RecalculateKitUsageConstants";
/*
 * The shared operation sheet — the filter sizing below, and the Preview popup's flex layout.
 *
 * It lives under wm/components/charges/common/ because that is where the first screens to need it sit,
 * and its own header records that it is "not charges-specific, only charges-located". This is the first
 * IM screen to use it, so the import now crosses modules rather than only reaching sideways within WM.
 * Moving it somewhere module-neutral is worth doing, but it touches the six screens already importing
 * it and belongs in its own change.
 *
 * There is no stylesheet of this screen's own: everything it needs, including its --op-chrome, is in
 * the sheet. See .op-filter-only there.
 */
import "../../wm/components/charges/common/operationScreen.css";

let accessToken = "";

/**
 * Puts a class on the Preview popup's WRAPPER so the shared operationScreen.css can reach
 * `.dx-popup-content` — DevExtreme renders a Popup into an overlay outside this component's own DOM
 * subtree, so there is no other hook onto it.
 *
 * Module-level, not an inline object literal: devextreme-react compares props by identity, and a fresh
 * object every render makes it re-apply the option on each one.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * IM "Recalculate Kit Usage Details" (legacy IMD0086).
 *
 * A batch/process screen: selection criteria, then Start / Preview / Reset. There is no entity grid and
 * nothing is edited here, so the DevExtreme inline-edit rules (Architecture.md 4-6, 14-16) do not apply.
 *
 * THE CRITERIA PANEL IS <CommonReport>, NOT HAND-WRITTEN. It renders the Available Fields list and the
 * criteria grid from the report definition named by REPORT_NAME - the server-side
 * `RecalculateKitUsage : BaseReport`, whose WhereInfoList doubles as the SQL allow-list. That
 * component owns everything the legacy `TAbWhereBuilder` owned: which fields are offered, the
 * operator / prefix / group / order-by editors, per-field lookups, the Flag code-to-description
 * translation, and saved range macros.
 *
 * Legacy button mapping:
 *   Start   -> POST StartKitUsageRecalculation   (runs SPIM_RECALCULATEKITUSAGE)
 *   Preview -> POST PreviewKitUsageRecalculation (read-only rowset, like TAbWhereBuilder.Preview)
 *   Reset   -> CommonReport.loadReport()         (like TAbWhereBuilder.Reset: reload the criteria,
 *                                                 touch no kit data)
 */
class RecalculateKitUsage extends React.Component {
    constructor(props) {
        super(props);
        accessToken = props.oidc.user.access_token;

        // Reaches CommonReport's instance so getRangeFilters() / loadReport() can be called on it.
        this.reportRef = React.createRef();

        this.initialState = {
            // Mirrors CommonReport's report object via onReportStateChange. Used only as the fallback
            // filter source described in committedFilterRows.
            report: { filterWhereInfoList: [] },
            previewData: null,
            isPreviewPopupVisible: false,
            loadIndicatorVisible: false,
        };
        this.state = { ...this.initialState };
    }

    handleReportStateChange = (reportState) => {
        this.setState({ report: reportState });
    };

    /**
     * The criteria rows to submit.
     *
     * Must go through `CommonReport.getRangeFilters()` rather than the array handed back by
     * `onReportStateChange`: that array is stale at submit time. If the user types into a criteria cell
     * and clicks Start without tabbing out, DevExtreme has not written the editor value back to the row
     * yet, so the posted filter silently lacks it - and on THIS screen a filter that arrives empty means
     * "recalculate every kit in the company". getRangeFilters awaits commitPendingGridEdits() first
     * (saveEditData on every filter grid), maps flag descriptions back to their codes, and drops
     * value-less rows. See common/reportFilters.js, which carries the full reasoning behind the hook
     * version of this that the WM charges screens use.
     *
     * The state fallback only covers a mount race, so it degrades rather than throwing.
     */
    committedFilterRows = async () => {
        const report = this.reportRef.current;

        if (report && typeof report.getRangeFilters === "function") {
            return await report.getRangeFilters();
        }

        return this.state.report?.filterWhereInfoList || [];
    };

    /**
     * The request body both endpoints take. IgnoreWarnings follows the API's first-submit default.
     *
     * The criteria are the whole payload: the operation takes no program/source code, because the legacy
     * Pascal passes only company, module and the where fragment and nothing on the server reads one. See
     * RecalculateKitUsageDTO.
     */
    buildRequest = (filterWhereInfoList) => ({
        filterWhereInfoList: filterWhereInfoList,
        ignoreWarnings: true,
    });

    postOperation = (route, filterWhereInfoList) =>
        axios.post(
            `${window._env_.REACT_APP_APIS}/${route}`,
            JSON.stringify(this.buildRequest(filterWhereInfoList)),
            {
                headers: {
                    "Content-Type": "application/json",
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                },
                timeout: REQUEST_TIMEOUT_MS,
            }
        );

    /**
     * Reports an outcome through DevExtreme's own `alert` dialog, the same module this screen already
     * takes `confirm` from.
     *
     * Not the shared `WarningMessagePopup`: that is a hand-rolled Popup whose Ok button sits wherever
     * its content ends, and it would need custom CSS to place it. `alert` puts its button in the
     * popup's bottom toolbar at `location: "center"` (devextreme/ui/dialog), so it is centred on the
     * bottom edge with no styling of ours, and it matches every other dialog in the app.
     *
     * The title carries the legacy distinction the old single "Message" caption lost: uIMD0086.pas
     * called InsightTaskHelper.PutMessage with lmtInformation for the two non-failure results and
     * lmtError for the failure.
     *
     * The LoadPanel is hidden in the setState callback, not before it, so its shading is gone before
     * the dialog paints rather than the two overlays sharing the screen for a frame.
     */
    showMessage = (message, title = MESSAGES.INFORMATION) => {
        this.setState({ loadIndicatorVisible: false }, () => alert(message, title));
    };

    startButtonClicked = async () => {
        const filterRows = await this.committedFilterRows();

        // An empty criteria set is a legal request that recalculates every kit in the company (the
        // legacy passes SQL NULL for @inWhere and asks nothing), so confirm first. getRangeFilters has
        // already dropped value-less rows, so a non-empty array here means real criteria.
        if (filterRows.length === 0) {
            const proceed = await confirm(
                MESSAGES.KIT_USAGE_RECALCULATION_CONFIRM_ALL_KITS,
                MESSAGES.CONFIRM
            );
            if (!proceed) {
                return;
            }
        }

        this.setState({ loadIndicatorVisible: true });

        try {
            const response = await this.postOperation(
                routes.IM.StartKitUsageRecalculation,
                filterRows
            );
            const payload = response.data ?? {};
            // The two success outcomes - batch number and nothing to do - arrive as the server's own
            // message, and nothing is reworded here. FAILED does not reach this line: the service sets
            // Success = false, the controller maps that to a 400 through MapServiceFailure, and axios
            // rejects, so it is handled in the catch below (getApiErrorMessage reads body.message).
            this.showMessage(payload.Message || payload.Resultdata?.Message || MESSAGES.ERROR);
        } catch (error) {
            this.showMessage(getApiErrorMessage(error, MESSAGES.ERROR), MESSAGES.ERROR);
        }
    };

    previewButtonClicked = async () => {
        const filterRows = await this.committedFilterRows();

        this.setState({ loadIndicatorVisible: true });

        try {
            const response = await this.postOperation(
                routes.IM.PreviewKitUsageRecalculation,
                filterRows
            );
            const payload = response.data ?? {};
            const rows = payload.Resultdata ?? [];

            if (rows.length === 0) {
                // The server sends Messages.ReportNoRecordsFound here, so an empty grid is never opened.
                this.showMessage(payload.Message || MESSAGES.NO_RECORD_FOUND_RANGE);
                return;
            }

            this.setState({
                previewData: rows,
                isPreviewPopupVisible: true,
                loadIndicatorVisible: false,
            });
        } catch (error) {
            this.showMessage(getApiErrorMessage(error, MESSAGES.ERROR), MESSAGES.ERROR);
        }
    };

    /**
     * Legacy btnResetFilterClick / TAbWhereBuilder.Reset: back to the criteria the screen opened with,
     * touching no kit data.
     *
     * Delegated to CommonReport.loadReport(), which re-fetches the report definition and rebuilds both
     * the Available Fields list and the criteria grid from it. Resetting only this component's state
     * would leave whatever criteria the user had entered sitting in CommonReport.
     */
    resetButtonClicked = () => {
        this.setState({ ...this.initialState });

        const report = this.reportRef.current;
        if (report && typeof report.loadReport === "function") {
            report.loadReport();
        }
    };

    handleClosePreviewPopUp = () => {
        this.setState({ isPreviewPopupVisible: false });
    };

    render() {
        return (
            <>
                <div className="row">
                    <div className="col-md-12 main-page">
                        {/*
                          * A plain Toolbar rather than the OM processing screens' Accordion: those wrap
                          * a band of process parameters that is worth collapsing, and this screen has
                          * none - the criteria panel below is its only input, so an accordion body here
                          * would be empty.
                          *
                          * className MUST be "header", NOT "accordian-toolbar". The latter is only for a
                          * toolbar rendered inside an accordion ITEM TITLE: App.scss forces
                          * `.accordian-toolbar .dx-toolbar-items-container { height: 0px !important }`
                          * and the themes then position its halves absolutely (.dx-toolbar-after
                          * right: 40px, .dx-toolbar-before left: 17px), because the accordion title
                          * supplies the height. Used standalone it collapses to zero height and every
                          * button disappears. `.header` (position: relative; align-items: center) is the
                          * standalone page-toolbar class - see WM Calculate Deferred Revenue.
                          */}
                        <Toolbar className="header">
                            <Item location="before">
                                <div className="title">
                                    <h6 style={{ color: window._env_.Color2 }}>{PAGE_TITLE}</h6>
                                </div>
                            </Item>
                            <Item location="after">
                                <Button
                                    className="accordianButton"
                                    text={BUTTON.Start}
                                    onClick={this.startButtonClicked}
                                    type="default"
                                />
                            </Item>
                            <Item location="after">
                                <Button
                                    className="accordianButton"
                                    text={BUTTON.Preview}
                                    onClick={this.previewButtonClicked}
                                />
                            </Item>
                            <Item location="after">
                                <Button
                                    className="accordianButton"
                                    text={BUTTON.Reset}
                                    onClick={this.resetButtonClicked}
                                />
                            </Item>
                        </Toolbar>
                    </div>
                </div>

                {/*
                  * .op-filter-only / .op-filter size the criteria panel - both from the shared sheet.
                  * Left bare, ReportFilter's Available Fields list and criteria grid pick up two
                  * DIFFERENT viewport-based heights from the theme, so their bottom edges do not line
                  * up, and both offsets assume a band of parameter controls this screen does not have -
                  * which left a wide dead strip under the panel. The wrapper drives both from the
                  * chrome this page really has.
                  *
                  * .op-filter-only is the sheet's own value for a page that is nothing but its filter,
                  * which is exactly this screen, so there is no number to declare here.
                  */}
                <div className="op-filter-only">
                    <div className="op-filter">
                        {/*
                          * showMacroButtons is left off, as on the sibling process screens. CommonReport
                          * can offer saved range macros - arguably the modern form of the legacy screen's
                          * ProfileComment-bound Comment box, which nothing in uIMD0086.pas ever persisted
                          * - but turning it on adds a feature the legacy screen did not have, so it is a
                          * product decision rather than part of this migration.
                          */}
                        <CommonReport
                            name={REPORT_NAME}
                            fullName={REPORT_FULL_NAME}
                            onReportStateChange={this.handleReportStateChange}
                            innerRef={this.reportRef}
                        />
                    </div>
                </div>

                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    onHiding={() => this.setState({ loadIndicatorVisible: false })}
                    visible={this.state.loadIndicatorVisible}
                    showIndicator={true}
                    shading={true}
                    showPane={true}
                    hideOnOutsideClick={false}
                />

                {this.state.isPreviewPopupVisible && (
                    <Popup
                        visible={this.state.isPreviewPopupVisible}
                        onHiding={this.handleClosePreviewPopUp}
                        dragEnabled={false}
                        hideOnOutsideClick={true}
                        showCloseButton={true}
                        showTitle={true}
                        title={PREVIEW_TITLE}
                        container=".dx-viewport"
                        // A definite size, matching the sibling preview dialogs. The grid inside is
                        // told to fill its parent, and "fill" needs something to resolve against - with
                        // the popup left to size itself to its content there is no height to inherit.
                        width="94vw"
                        height="76vh"
                        wrapperAttr={PREVIEW_POPUP_ATTR}
                    >
                        {/*
                          * The grid FILLS the popup, with the row count as its sibling below - see
                          * .op-preview-popup in the shared operationScreen.css. Flexing rather than
                          * giving the grid height="calc(76vh - N)": that N has to guess the popup's own
                          * chrome plus this footer, and guessing high leaves a dead strip under the
                          * count while guessing low clips it.
                          *
                          * No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller
                          * would give it an unbounded width to lay out against, killing horizontal
                          * scrolling across the 26 visible columns.
                          */}
                        <div className="op-preview-body">
                            <div className="op-preview-grid">
                                <RecalculateKitUsageGridPreview dataSource={this.state.previewData} />
                            </div>

                            {/*
                              * The row count, as TLookupForm shows it under its grid (SearchFm.pas
                              * lbInfo), and the reason the grid below is unpaged: a count that reports
                              * the whole result set sitting under a grid showing one page of it is two
                              * different numbers on one dialog.
                              *
                              * Counted from previewData, i.e. the whole result set - not the grid's
                              * filter-row state - which is what Delphi's qrySearch.RecordCount is.
                              *
                              * Guarded even though previewButtonClicked never opens the popup on an
                              * empty rowset: it reports the server's own no-records message instead, so
                              * a zero here would be a second, differently-worded way of saying it.
                              */}
                            {this.state.previewData?.length > 0 && (
                                <div className="op-preview-footer">
                                    {MESSAGES.PREVIEW_RECORDS_MATCHING_FORMAT.replace(
                                        "{0}",
                                        this.state.previewData.length
                                    )}
                                </div>
                            )}
                        </div>
                    </Popup>
                )}
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(RecalculateKitUsage);
